import { Box } from "@mui/material";
import AppModel from "models/AppModel";
import CurrentLocationModel from "models/CurrentLocationModel";
import { useSnackbar } from "notistack";
import { Feature, MapEvent } from "ol";
import { circular } from "ol/geom/Polygon";
import VectorLayer from "ol/layer/Vector";
import { toLonLat } from "ol/proj";
import VectorSource from "ol/source/Vector";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppTheme } from "theme";
import { requests } from "utils/requests";
import { IPoint } from "utils/types";
import OLMap, { IOLMapProps } from "../map";
import { MapClass } from "../map/map";
import { AddressSelectionType, IRoutePoint } from "../routePoints/types";
import CurrentLocationButton from "./currentLocationButton";
import { MapPointer } from "./mapPointer";
import { getDistance } from 'ol/sphere';


export interface IArea {
  point: IPoint;
  radius: number;
}

interface IProps {
  onChange?: (value: IRoutePoint | null) => void;
  children?: JSX.Element;
  mapProps?: IOLMapProps;
  forceCenter?: IPoint;
  readOnly?: boolean;
  area?: IArea;
  getOnlyPoint?: boolean;
  onMapMoveEnd?: (event: MapEvent) => void;
  onMapInit?: (map: MapClass) => void;
}

export default function AddressOnMapField(props: IProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [map, setMap] = useState<MapClass | undefined>(undefined);
  const [center, setCenter] = useState<IPoint | undefined>(undefined);
  const [mapMarginBottom, setMapMarginBottom] = useState<number>(0);
  const [mapMoving, setMapMoving] = useState<boolean>(false);
  const [pointerPosition, setPointerPosition] = useState<[number, number] | null>(null);
  const [addressSelectionType, setAddressSelectionType] = useState<AddressSelectionType>(AddressSelectionType.init);
  const [pressCurrentLocation, setPressCurrentLocation] = useState<boolean | null>(null);
  const pointerPixelRef = useRef(null);
  const infoRef = useRef(null);
  const mapRef = useRef(null);

  const {
    onChange,
    mapProps,
    forceCenter,
    children,
    readOnly,
    getOnlyPoint,
    onMapInit,
    area,
  } = props;

  const onMapMoveEnd = (m: MapEvent) => {
    setMapMoving(false);
    if (props.onMapMoveEnd !== undefined) {
      props.onMapMoveEnd(m);
    }

    if (!!readOnly) return;

    if (pointerPixelRef && pointerPixelRef?.current) {
      let position = (pointerPixelRef?.current as any).getBoundingClientRect();
      const x = position.x;
      const y = position.y - ((mapRef?.current as any).getBoundingClientRect().y || 0);
      const coordinates = toLonLat(m.map.getCoordinateFromPixel([x, y]));

      if(!!area){
        const center = getLonLat(area.point);
        const distance = getDistance(center, coordinates);
        if (distance > area.radius && !!pointerPosition) {
          map?.setOnlyCenter({
            type: 'Point',
            coordinates: [pointerPosition[1], pointerPosition[0]],
          });
          return;
        }
      }

      setPointerPosition([coordinates[0], coordinates[1]])
    }
  }

  const updateNear = useCallback(() => {
    if (pointerPosition === null) return;
    if(!!getOnlyPoint){
        onChange?.(
          {
            point: {
              type: 'Point',
              coordinates: [
                pointerPosition[1],
                pointerPosition[0]
              ]
            },
            selection_type: addressSelectionType
          }
        )
        return;
    }

    const abortController = new AbortController();
    let timer = setTimeout(() => {
      requests.get("/address/near/", {
        limit: 1,
        lng: pointerPosition[0],
        lat: pointerPosition[1],
      }, abortController.signal).then((r) => {
        let res: IRoutePoint | null = null;
        if (!r.body?.count) {
          enqueueSnackbar(t('address_not_found'), { variant: 'warning' })
        } else {
          res = r.body.results[0]
        }

        if (res !== null) {
          res.point = {
            type: 'Point',
            coordinates: [
              pointerPosition[1],
              pointerPosition[0]
            ]
          }
        }

        onChange?.({ ...res, selection_type: addressSelectionType })
      })
    }, 200)

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };

  }, [addressSelectionType, pointerPosition, getOnlyPoint, enqueueSnackbar, t, onChange])

  useEffect(updateNear, [updateNear])

  useEffect(() => {
    if(readOnly && !!forceCenter){
      setPointerPosition([forceCenter.coordinates[1], forceCenter.coordinates[0]])
    }
  }, [readOnly, forceCenter])

  const updateMapMarginBottom = useCallback(() => {
    if (infoRef !== null && infoRef.current !== null) {
      setMapMarginBottom((infoRef.current as any).clientHeight)
    }
  }, [infoRef])

  useEffect(() => {
    if (map === null) return;
    if (mapProps) { };
    updateMapMarginBottom();
  }, [map, mapProps, updateMapMarginBottom])

  const setCurrentLocation = useCallback(() => {
    if (map === null) return;

    updateMapMarginBottom();
    if (CurrentLocationModel.point) {
      setCenter({ ...CurrentLocationModel.point });
    } else {
      if (AppModel.city?.center) {
        setCenter({ ...AppModel.city?.center });
      }
    }
  }, [map, updateMapMarginBottom])

  useEffect(() => {
    if (center) return;

    if (CurrentLocationModel.point !== null) {
      setCurrentLocation();
      return
    }

    let timeout: any = 0;
    const updateCenter = (point: IPoint) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (!center) {
          setCurrentLocation();
        }
      }, 200)
    }

    CurrentLocationModel.subscribe(updateCenter);
    return () => {
      CurrentLocationModel.unsubscribe(updateCenter);
    }
  }, [center, setCurrentLocation])

  useEffect(() => {
    if (forceCenter !== undefined) {
      if (map !== undefined) {
        const event = new MapEvent("moveend", map.map);
        try {
          map.map.dispatchEvent(event)
        } catch { }
      }
      setCenter(forceCenter);
    }
  }, [forceCenter, map])

  const rndID = useMemo(() => {
    return `map-id-${Math.random()}`;
  }, [])

  const getLonLat = useCallback((point: IPoint) => {
    return [point.coordinates[1], point.coordinates[0]];
  }, []);

  useEffect(() => {
    if (!area || !map) return;

    const center = getLonLat(area.point);
    const circle = circular(center, area.radius, 64);
    circle.transform('EPSG:4326', 'EPSG:3857');

    const circleFeature = new Feature(circle);
    const vectorSource = new VectorSource({
      features: [circleFeature]
    });
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      style: new Style({
        stroke: new Stroke({
          color: AppTheme.darkColor,
          width: 2
        })
      })
    });

    map.map.addLayer(vectorLayer);
    const extent = circleFeature.getGeometry()?.getExtent();

    setTimeout(() => {
      if (!!extent) {
        map.setFit(extent);
      } else {
        map.setCenter(area.point);
      }
    }, 1);
    return () => {
      map.map.removeLayer(vectorLayer);
    }
  }, [area, getLonLat, map])

  const _onMapInit = useCallback((m: MapClass) => {
    if (onMapInit !== undefined) {
      onMapInit(m)
    }
    setMap(m)
  }, [onMapInit])

  return (
    <Box
      ref={mapRef}
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        left: 0,
        bottom: 0
      }}
    >
      <OLMap
        {...(mapProps || {})}
        id={rndID}
        center={!mapProps?.route ? center : undefined}
        currentLocation={true}
        includeCurrentLocationToFit={false}
        marginBottom={mapMarginBottom}
        onInit={_onMapInit}
        onMoveStart={(m) => {
          setMapMoving(true);
        }}
        onMoveEnd={(m) => {
          if (pressCurrentLocation !== null) {
            setAddressSelectionType(pressCurrentLocation ? AddressSelectionType.current : AddressSelectionType.move);
          }
          setPressCurrentLocation(false);
          onMapMoveEnd(m);
        }}
      />

      {children ? (
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <Box
            flexGrow={1}
            flexShrink={1}
            sx={{
              position: 'relative'
            }}
          >
            {!readOnly ? (
              <MapPointer
                ref={pointerPixelRef}
                active={mapMoving}
              />
            ) : null}
          </Box>
          <Box
            flexGrow={0}
            flexShrink={0}
          >
            <Box
              ref={infoRef}
              sx={{
                position: 'relative',
                pb: 2,
              }}
            >
              <CurrentLocationButton
                onClick={() => {
                  setPressCurrentLocation(true);
                  setAddressSelectionType(AddressSelectionType.current);
                  setCurrentLocation();
                }}
              />

              {children}
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}